import { Stack, Box, SxProps, Theme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useScreenLargerThan } from '../lib/hooks/breakpoint-hooks';
import useTrackPageView from '../lib/hooks/use-track-page-view';
import AuditList from '../components/Audit/AuditList/AuditList';
import { AuditLogModel } from '../lib/models/AuditModel';
import { post } from '../lib/api';
import { SearchProps } from '../lib/dtos/SearchProps';
import SearchBox from '../components/SearchBox/SearchBox';
import { useDebounce } from 'usehooks-ts';
import { AxiosResponse } from 'axios';
import { INVALID_DATE } from '../components/Utils/InternalDatePicker';
import moment from 'moment';
import { useBeforeUnload, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useURLState from '../components/Audit/useURLState';
import { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import {
    deserializeFilterModel,
    serializeFilterModel,
    deserializeSortModel,
    serializeSortModel
} from '../components/Audit/AuditList/serializationHelperFunctions';

const AuditPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams(location.search);

    const trackPageView = useTrackPageView();

    const [data, setData] = useState<AuditLogModel>({ data: [], total: 0 });
    const [loading, setLoading] = useState(false);

    const auditPageUrl = sessionStorage.getItem('auditPageUrl');
    const [searchTextStore, startDateStore, endDateStore] = auditPageUrl
        ? auditPageUrl.split(':')
        : ['', '', ''];

    const auditListUrl = sessionStorage.getItem('auditListUrl');
    const [filterStore, sortStore] = auditListUrl ? auditListUrl.split(':') : ['', ''];

    const [filterValue, setFilterValue] = useURLState<string | undefined>(
        'searchText',
        '',
        searchParams,
        searchTextStore !== '',
        {
            decode: (val) => val
        }
    );

    const [filterModel, setFilterModel] = useURLState<GridFilterModel | undefined>(
        'filterModel',
        undefined,
        searchParams,
        filterStore !== '',
        {
            encode: serializeFilterModel,
            decode: deserializeFilterModel
        }
    );
    const [sortModel, setSortModel] = useURLState<GridSortModel | undefined>(
        'sortModel',
        undefined,
        searchParams,
        sortStore !== '',
        {
            encode: serializeSortModel,
            decode: deserializeSortModel
        }
    );

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel | undefined>({
        page: 0,
        pageSize: 10
    });

    const debouncedFilterValue = useDebounce(filterValue, 300);
    const defaultSearchDate = useMemo(() => moment().add(-90, 'day').format("YYYY-MM-DD"), []);

    const [startDateValue, setStartDateValue] = useURLState(
        'startDate',
        moment().add(-1, 'day').format("YYYY-MM-DD"),
        searchParams,
        startDateStore !== '',
        {
            decode: (val) => val
        }
    );
    const [endDateValue, setEndDateValue] = useURLState(
        'endDate',
        moment().format("YYYY-MM-DD"),
        searchParams,
        endDateStore !== '',
        {
            decode: (val) => val
        }
    );
    const [startDateChanged, setStartDateChanged] = useState(false);

    useEffect(() => {
        sessionStorage.removeItem('auditPageUrl');
    }, []);

    useBeforeUnload(() => {
        sessionStorage.setItem('auditPageUrl', `${filterValue}:${startDateValue}:${endDateValue}`);
    });

    const isLargerThanSmall = useScreenLargerThan('sm');

    const handleStartDateChange = useCallback(
        (startDate: string, startDateChanged?: boolean) => {
            setStartDateValue(startDate);
            setStartDateChanged(startDateChanged ?? false);
        },
        [setStartDateValue, setStartDateChanged]
    );

    useEffect(() => {
        if (
            startDateValue.toString().toLowerCase() !== INVALID_DATE.toLowerCase() &&
            endDateValue.toString().toLowerCase() !== INVALID_DATE.toLowerCase()
        ) {
            navigate({ search: searchParams.toString() }, { replace: true });
        }
    }, [searchParams, navigate, startDateValue, endDateValue]);

    useEffect(() => {
        const handler = async () => {
            if (
                startDateValue.toString().toLowerCase() !== INVALID_DATE.toLowerCase() &&
                endDateValue.toString().toLowerCase() !== INVALID_DATE.toLowerCase()
            ) {
                if (!startDateChanged && debouncedFilterValue !== '')
                    handleStartDateChange(defaultSearchDate, true);

                setLoading(true);
                const handleFilterModel = (filterModel: GridFilterModel | undefined) => {
                    const items = [...(filterModel?.items.map((i) => ({ ...i })) ?? [])];

                    const handledItems = items.map((i) => {
                        if (i.operator === 'isAnyOf') {
                            if (i.value && i.value instanceof Array) {
                                const value = i.value.map((v) => v.toLowerCase());
                                i.value = value.join(',');
                            }
                        } else if (i.operator === 'isEmpty') {
                            i.value = 'isEmpty';
                        } else if (i.operator === 'isNotEmpty') {
                            i.value = 'isNotEmpty';
                        }
                        return i;
                    });

                    if (handledItems?.every((i) => i.value)) {
                        return {
                            ...filterModel,
                            items: handledItems
                        };
                    }

                    return undefined;
                };

                const payload = {
                    search: debouncedFilterValue,
                    startDate: startDateValue,
                    endDate: endDateValue,
                    paginationModel: {
                        ...paginationModel,
                        page: paginationModel && paginationModel.page + 1
                    },
                    sortModel,
                    filterModel: handleFilterModel(filterModel),
                };

                const data = await post<SearchProps, AxiosResponse<AuditLogModel>>(
                    'audit/getList',
                    payload
                );
                setLoading(false);
                setData(data.data);
            }
        };
        handler();
    }, [
        debouncedFilterValue,
        startDateValue,
        endDateValue,
        paginationModel,
        sortModel,
        filterModel,
        startDateChanged,
        handleStartDateChange,
        defaultSearchDate
    ]);

    React.useEffect(() => {
        trackPageView('Audit Page');
    }, [trackPageView]);

    const handleSearchTextChange = (filter: string) => {
        setFilterValue(filter);
    };

    const handleEndDateChange = (endDate: string) => {
        setEndDateValue(endDate);
    };


    return (
        <Box sx={containerStyle(isLargerThanSmall)}>
            <SearchBox
                page="audit"
                setFilter={handleSearchTextChange}
                filterValue={filterValue ?? ''}
            />
            <Stack direction={isLargerThanSmall ? 'row' : 'column'} gap={1}>
                <AuditList
                    data={data}
                    loading={loading}
                    inputDateProps={{
                        startDate: startDateValue,
                        setStartDate: handleStartDateChange,
                        endDate: endDateValue,
                        setEndDate: handleEndDateChange
                    }}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                />
            </Stack>
        </Box>
    );
};

const containerStyle: (media: boolean) => SxProps<Theme> = (media: boolean) => {
    const biggerThanSm: SxProps<Theme> = { padding: 3 };
    const smallerThanSm: SxProps<Theme> = {
        paddingX: 0
    };

    return media ? biggerThanSm : { width: '100%', ...biggerThanSm, ...smallerThanSm };
};

export default AuditPage;
